import { Dialog } from "@headlessui/react";
import { useEffect, useState } from "react";
import { Configure, connectHits, InstantSearch, Pagination, SearchBox, SortBy } from 'react-instantsearch-dom';
import Select from "react-select";
import { UserStatus } from "../../models/enumerations/user-status";
import FirestoreCondition from "../../models/interfaces/firestore-condition";
import { User } from "../../models/interfaces/user";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import searchClient, { SearchIndexes } from "../../utilities/search-client";
import UserService from "../../utilities/services/user-service";
import { Utils } from "../../utilities/utils";
import { Button } from "../button/button";
import EmailList from "../email-list/email-list";
import { Loader } from "../loader/loader";
import { Modal } from "../modal/modal";
import { Tag as TagComponent } from "../tag/tag";
import { Tag } from "../../models/interfaces/tag";
import UserUtil from "../../utilities/user-util";
import StringUtil from "../../utilities/string-util";
import TagService from "../../utilities/services/tag-service";
import { ListOptions } from "../../models/interfaces/list-options";
import { Group } from "../../models/interfaces/group";
import GroupService from "../../utilities/services/group-service";
import { UserTag } from "../../models/interfaces/user-tag";
import { SearchHits } from "./search-hits";
import { Organization } from "../../models/interfaces/organization";
import UserRoles from "../../models/user-roles";
import { UniqueUserInviteLink } from "../../models/interfaces/unique-user-invite-link";
import { normalizeEmail } from "../../utilities/email-util";
import UniqueUserInviteLinksService from "../../utilities/services/unique-user-invite-link-service";
import { enqueueSnackbar } from "notistack";
import { SymmioAccessType } from "../../models/enumerations/symmio-access-type";
import { SymmioLicenseCheckbox } from "../symmio-license-checkbox";
import { ToggleButton } from "../forms";
import { useTranslation } from 'react-i18next';
import OrganizationSubscriptionService from "../../utilities/services/organization-subscription-service";
import { AddOnId } from "../../models/enumerations/add-on-id";
import BillingService from "../../utilities/services/billing-service";
import { createLogger } from "../../utilities/logger";
import { useUserCreationLimits } from "../../utilities/hooks/use-user-creation-limits";
import OrganizationUtil from "../../utilities/organization-util";

const log = createLogger("Clients");

const COMPONENT_CLASS = "c-user-list";

interface ClientsProps {
    organization: Organization;
    products: any[];
    setOrganization: Function;
}

const Clients: React.FC<ClientsProps> = (props: ClientsProps) => {
    const {
        organization,
        products,
        setOrganization,
    } = props;
    const [isSaving, setIsSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [userToDelete, setUserToDelete] = useState<any>({});
    const [newEmails, setNewEmails] = useState([] as string[]);
    const [errorMessages, setErrorMessages] = useState([] as string[]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [selectedTags, setSelectedTags] = useState<UserTag[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<any>();
    const [selectedManagedGroups, setSelectedManagedGroups] = useState<string[]>();
    const [, setSelectedRole] = useState<any>();
    const [algoliaFilters, setAlgoliaFilters] = useState<string>(`organizationId:"NO_MATCH"`);
    const [searchAllOrgs, setSearchAllOrgs] = useState<boolean>(false);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const [isSymmioAccess, setIsSymmioAccess] = useState<boolean>(false);
    const {
        overUserLimit,
        refreshLimits,
        checkAccountHolderLimit,
        validateRemainingCapacity,
        getOverLimitMessage,
    } = useUserCreationLimits();
    const [activeUsers, setActiveUsers] = useState<number>();
    const [inactiveUsers, setInactiveUsers] = useState<number>();
    const [pendingUsers, setPendingUsers] = useState<number>();
    let invalidEmails: string[] = [];
    const [searchState, setSearchState] = useState({
        sortBy: SearchIndexes.usersByNameAsc
    });
    const { state } = useAuthState();
    const isSuperAdmin = UserUtil.isSuperAdmin(state.claims);
    const isAdmin = UserUtil.isAdmin(state.user);
    const isManager = UserUtil.isManager(state.user);
    const isAccountHolder = UserUtil.isAccountHolder(state.user);
    const isSubOrg = OrganizationUtil.isSubOrganization(organization);
    const hasSubOrgs = (organization?.subOrganizationIds?.length ?? 0) > 0;
    const hasAccessibleSubOrgs = (state.user?.accessibleSubOrganizationIds?.length ?? 0) > 0;
    const canSearchAllOrgs = isSuperAdmin
        || (!isSubOrg && ((isAccountHolder && hasSubOrgs) || (isAdmin && hasAccessibleSubOrgs)));
    const CustomHits = connectHits(SearchHits);
    const { t } = useTranslation();

    useEffect(() => {
        if (open) {
            refreshLimits(organization);
        }
    }, [state.user, organization, open, refreshLimits]);

    useEffect(() => {
        const getUserStatus = async () => {
            let users: User[] = [];

            if (isManager) {
                const managedGroupIds = await UserUtil.getHierarchicalGroupIds(state.user?.managedGroupIds);

                if (managedGroupIds.length !== 0) {
                    users = await UserService.getBy([
                        {
                            field: "organizationId",
                            operator: "==",
                            value: state.organization.id,
                        },
                        {
                            field: "groupId",
                            operator: "in",
                            value: managedGroupIds,
                        },
                        {
                            field: "roles",
                            operator: "array-contains",
                            value: "client"
                        }
                    ]);
                }
            }
            else {
                users = await UserService.getBy([
                    {
                        field: "organizationId",
                        operator: "==",
                        value: state.organization.id,
                    },
                    {
                        field: "roles",
                        operator: "array-contains",
                        value: "client"
                    }
                ]);
            }

            setActiveUsers(users?.filter((u) => u.status === UserStatus.Active && u.authenticationId != null).length || 0);
            setInactiveUsers(users?.filter((u) => u.status === UserStatus.Inactive && u.authenticationId != null).length || 0);
            setPendingUsers(users?.filter((u) => u.status !== UserStatus.Disabled && u.authenticationId == null).length || 0);
        }


        getUserStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (state?.user == null || state?.organization?.id == null) {
            return;
        }

        const loadData = async () => {
            TagService.getBy([{
                field: "organizationId",
                operator: "==",
                value: state.organization.id
            }], [{
                field: "name",
                direction: "asc",
            }]).then((tags: Tag[]) => {
                setTags(tags);
            });

            GroupService.getBy([{
                field: "organizationId",
                operator: "==",
                value: state.organization.id
            }], [{
                field: "name",
                direction: "asc",
            }]).then((groups: Group[]) => {
                setGroups(groups);
            });

            setAlgoliaFilters(await UserUtil.getAlgoliaUserOrganizationAndGroupFilterByClient(
                state.user,
                searchAllOrgs ? undefined : state.organization.id,
            ));

        }

        loadData();
    }, [state.user, state.organization, searchAllOrgs]);

    const handleAddUserClick = () => {
        setErrorMessages([]);
        setOpen(true);
    }

    const handleSymmioAccessCheckbox = () => {
        setIsSymmioAccess(!isSymmioAccess);
    }

    const handleDeleteUserClick = async () => {
        if (userToDelete?.objectID == null) {
            return;
        }

        setIsSaving(true);

        try {
            await UserService.deleteById(userToDelete.objectID);
            log.info("Member removed", {
                subjectUserId: userToDelete.objectID,
                organizationId: state.organization?.id,
            });
        } catch (e) {
            log.error("Failed to remove member", {
                exception: e,
                subjectUserId: userToDelete.objectID,
                organizationId: state.organization?.id,
            });
        }

        setTimeout(() => {
            setOpenDelete(false);
            setUserToDelete(undefined);
            setIsSaving(false);
            searchClient.clearCache();
            enqueueSnackbar(t('users.user_del_confirm'), { variant: "toast", width: "450px" });
        }, 5000);

    }

    const isEmailUnique = async (email: string): Promise<boolean> => {

        let isUnique = true;
        const conditions = [{
            field: "email",
            operator: "==",
            value: normalizeEmail(email),
        }] as FirestoreCondition[];

        await UserService.getBy(conditions).then((result) => { isUnique = (result.length === 0) })
        return isUnique;
    }

    const handleSaveUserClick = async () => {
        const emails = newEmails.filter((email) => email.trim().length > 0 && Utils.validateEmailAddress(email.trim()));

        if (emails.length === 0) {
            errorMessages.push(t('forms.err_noEmails'))
            return;
        }

        // Re-validate against the organization's remaining capacity at submit
        // time so nothing is created when the limit has been reached (the
        // on-add validation can go stale between typing and submitting).
        const canProceed = await validateRemainingCapacity(organization, emails.length);
        if (!canProceed) {
            return;
        }

        setIsSaving(true);
        try {
            const orgSub = await OrganizationSubscriptionService.getByOrganizationId(organization.id!);
            if (orgSub && orgSub.length > 0 && orgSub[0].stripeAddOnSubscription) {
                const userAddOnPriceId = products
                    .find((item) => item.product.id === AddOnId.User)
                    ?.prices.find((item: any) => item.data.active === true)?.id;
                const licenseAddOnPriceId = products
                    .find((item) => item.product.id === AddOnId.License)
                    ?.prices.find((item: any) => item.data.active === true)?.id;

                if (userAddOnPriceId && licenseAddOnPriceId) {
                    await BillingService.checkAndUpdateHiddenLimit({
                        stripeAddOnSubscriptionId: orgSub[0].stripeAddOnSubscription,
                        userAddOnPriceId,
                        licenseAddOnPriceId,
                        currentLicenseCount: organization?.mySymmioLicenseCount ?? 0,
                    });
                }
            }
            for (const email of emails) {
                const conditions = [{
                    field: "email",
                    operator: "==",
                    value: normalizeEmail(email),
                }] as FirestoreCondition[];

                const usersByEmail = await UserService.getBy(conditions);
                if (!usersByEmail || usersByEmail.length > 0) {
                    continue;
                }

                const user = {
                    email: normalizeEmail(email),
                    groupId: selectedGroup?.value ?? null,
                    groupName: selectedGroup?.label ?? null,
                    organizationId: state.organization?.id,
                    status: UserStatus.Inactive,
                    tags: selectedTags,
                    roles: [UserRoles.CLIENT_ID],
                    symmioAccess: isSymmioAccess ? SymmioAccessType.AppLicense : SymmioAccessType.WebAccess,
                    managedGroupIds: selectedManagedGroups,
                    showWelcomeScreen: true,
                } as User;

                const savedUser = await UserService.save(user, state.user);

                const uniqueInviteLink = {
                    userId: savedUser.id,
                    senderEmail: state.user?.email,
                    organizationId: state.organization.id,
                } as UniqueUserInviteLink

                await UniqueUserInviteLinksService.save(uniqueInviteLink, state.user);

                log.info("User invited", {
                    subjectUserId: savedUser.id,
                    organizationId: state.organization?.id,
                    role: UserRoles.CLIENT_ID,
                    symmioAccess: user.symmioAccess,
                });
            }
        } catch (e) {
            log.error("Failed to invite user(s)", {
                exception: e,
                organizationId: state.organization?.id,
                emailCount: emails.length,
            });
        }
        enqueueSnackbar(t('users.user_add_confirm')
            , { variant: "toast", width: "550px", style: { whiteSpace: 'pre-line' } });
        setTimeout(() => {
            setOpen(false);
            setNewEmails([]);
            setIsSaving(false);
            searchClient.clearCache();
            setOrganization()
        }, 5000);
    }

    useEffect(() => {
        if (!open) {
            setNewEmails([]);
        }
    }, [open]);

    useEffect(() => {
        if (!openDelete) {
            setUserToDelete(undefined);
        }
    }, [openDelete]);

    // Keep the "search all organizations" toggle off whenever it is not
    // available (e.g. after switching into a sub-organization).
    useEffect(() => {
        if (!canSearchAllOrgs) {
            setSearchAllOrgs(false);
        }
    }, [canSearchAllOrgs, state.organization?.id]);

    return (
        <div className={`${COMPONENT_CLASS}`}>
            <Loader
                isVisible={isSaving} />

            { // if
                (canSearchAllOrgs) &&
                <ToggleButton
                    id="searchAllOrgsCheckbox"
                    onClick={(event) => { setSearchAllOrgs((event?.target as any)?.checked === true) }}
                    mainLabelText={t('buttons.btn_searchAllOrgs')} />

            }
            <InstantSearch
                indexName={SearchIndexes.usersByNameAsc}
                searchClient={searchClient}
                searchState={searchState}
                onSearchStateChange={setSearchState}>
                <Configure
                    filters={algoliaFilters}
                    hitsPerPage={50}
                />
                <SortBy
                    items={[
                        { label: t('users.sorting.nameAsc'), value: SearchIndexes.usersByNameAsc },
                        { label: t('users.sorting.nameDesc'), value: SearchIndexes.usersByNameDesc },
                        { label: t('users.sorting.groupAsc'), value: SearchIndexes.usersByGroupAsc },
                        { label: t('users.sorting.groupDesc'), value: SearchIndexes.usersByGroupDesc },
                        { label: t('users.sorting.statusAsc'), value: SearchIndexes.usersByStatusAsc },
                        { label: t('users.sorting.statusDesc'), value: SearchIndexes.usersByStatusDesc },
                        { label: t('users.sorting.accessAsc'), value: SearchIndexes.usersBySymmioAccessAsc },
                        { label: t('users.sorting.accessDesc'), value: SearchIndexes.usersBySymmioAccessDesc },
                    ]}
                    defaultRefinement="users"
                />
                <div style={{ display: "flex", flexFlow: "wrap-reverse", justifyContent: "space-between", alignItems: "center", gap: "1rem", marginTop: "1rem" }}>
                    <SearchBox
                        autoFocus
                        className={`${COMPONENT_CLASS}__search`}
                        searchAsYouType={true}
                        showLoadingIndicator={true}
                        loadingIndicator={<Loader isVisible={true} />}
                        translations={{
                            placeholder: t('general.search')
                        }}
                    />
                    <div style={{ display: "flex", flexFlow: "wrap", justifyContent: "center", gap: "8px" }}>
                        <div className={`${COMPONENT_CLASS}__info-card`}>
                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
                                <div style={{ fontSize: "24px", fontStyle: "normal", fontWeight: "700" }}>{activeUsers ? activeUsers : "0"}</div>
                                <div className="uppercase" style={{ margin: "auto 0", fontSize: "12px", fontStyle: "normal", fontWeight: "400", marginLeft: "8px" }}>{t('users.active')}</div>
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__info-card`}>
                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
                                <div style={{ fontSize: "24px", fontStyle: "normal", fontWeight: "700" }}>{inactiveUsers ? inactiveUsers : "0"}</div>
                                <div className="uppercase" style={{ margin: "auto 0", fontSize: "12px", fontStyle: "normal", fontWeight: "400", marginLeft: "8px" }}>{t('users.inactive')}</div>
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__info-card`}>
                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
                                <div style={{ fontSize: "24px", fontStyle: "normal", fontWeight: "700" }}>{pendingUsers ? pendingUsers : "0"}</div>
                                <div className="uppercase" style={{ margin: "auto 0", fontSize: "12px", fontStyle: "normal", fontWeight: "400", marginLeft: "8px" }}>{t('users.userPending')}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${COMPONENT_CLASS}__actions`}>
                        <Button
                            type="default"
                            buttonText={t('buttons.btn_addUsers')}
                            onClick={() => handleAddUserClick()} />
                    </div>
                </div>
                <CustomHits
                    setSearchState={setSearchState}
                    searchState={searchState}
                    SearchIndexes={SearchIndexes}
                    setUserToDelete={setUserToDelete}
                    isSuperAdmin={isSuperAdmin}
                    isAdmin={isAdmin}
                    isManager={isManager}
                    isAccountHolder={isAccountHolder}
                    setOpenDelete={setOpenDelete}
                    COMPONENT_CLASS={COMPONENT_CLASS}
                    isTeam={false}
                />
                <Pagination
                    className={`${COMPONENT_CLASS}__pagination`} />
            </InstantSearch >
            <Modal
                isOpen={open}
                isLoading={isSaving}
                onClose={() => {
                    setIsSymmioAccess(false);
                    setOpen(false);
                    setSelectedRole("")
                    setSelectedManagedGroups([]);
                }}
                title={t('users.addUsers')}
                defaultModalActions={true}
                submitDisabled={newEmails == null || newEmails.length === 0 || (!!overUserLimit) || (isSymmioAccess && (organization.mySymmioLicenseCount ?? 0) + newEmails.length > (organization.mySymmioLicense ?? 0))}
                onCancel={() => {
                    setOpen(false)
                    setSelectedRole("")
                    setSelectedManagedGroups([]);
                }}
                onSubmit={() => handleSaveUserClick()}
                submitButtonText={t('buttons.btn_sendInvites')}>
                <div className={errorMessages.length > 0 ? `${COMPONENT_CLASS}__fieldgroup-error` : `${COMPONENT_CLASS}__fieldgroup`}>
                    <label>{t('users.emails')}</label>
                    <EmailList
                        onEmailsAdded={(emails) => {
                            errorMessages.length = 0;

                            if (!isRunning) {
                                setIsRunning(true);

                                const result = [...newEmails];
                                emails = StringUtil.getUniqueValues(emails, true);

                                Promise.all(emails.map(async (email) => {
                                    if (await isEmailUnique(email) === false) {
                                        invalidEmails.push(" " + email);
                                    }
                                    else if (!StringUtil.existsInArray(email, result, true)) {
                                        result.push(email);
                                    }
                                })).then(async () => {

                                    if (invalidEmails.length !== 0) {
                                        let message = t('users.err_users_exist', { invalidEmails: invalidEmails.toString() });
                                        if (errorMessages.indexOf(message) === -1) {
                                            errorMessages.push(message);
                                        }
                                    }

                                    setNewEmails(result);
                                    await checkAccountHolderLimit(result.length);

                                    setIsRunning(false);
                                });
                            }

                        }}
                        onEmailsError={(errors) => { errorMessages.push(...new Set(errorMessages.concat(errors))); setErrorMessages(errorMessages); }}
                        enableSuggestions={false}
                    />
                    { // if
                        (errorMessages.length > 0 || overUserLimit) &&
                        <div className={`${COMPONENT_CLASS}__email-errors`}>
                            {errorMessages.map((error, i) => <div className={`${COMPONENT_CLASS}__email-errors__item`} key={`error-${i}`}>{error}</div>)}
                            {
                                (overUserLimit) &&
                                <div className={`${COMPONENT_CLASS}__email-errors__item`}>
                                    {getOverLimitMessage(isAccountHolder || isAdmin || isSuperAdmin)}
                                </div>
                            }
                        </div>
                    }
                </div>
                <div className={`${COMPONENT_CLASS}__fieldgroup-row`}>
                    <div className={`${COMPONENT_CLASS}__fieldgroup ${COMPONENT_CLASS}__fieldgroup-row-group`}>
                        <label htmlFor="group">{t('users.group')}</label>
                        <Select
                            classNamePrefix={"c-user-profile-header"}
                            isClearable={true}
                            name="group"
                            onChange={(newValue: any, actionMeta: any) => setSelectedGroup(newValue)}
                            options={groups.map((g) => ({ label: g.name, value: g.id }))}
                            placeholder={t('users.group')}
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    width: "100%",
                                    borderRadius: "1.5rem",
                                    lineHeight: "1.5rem",
                                    padding: "0.3rem 1.25rem"
                                }),
                            }} />
                    </div>
                    <div className={`${COMPONENT_CLASS}__fieldgroup ${COMPONENT_CLASS}__fieldgroup-row-tag`}>
                        <label htmlFor="tags">{t('users.tags')}</label>
                        <Select
                            classNamePrefix={"c-user-profile-header"}
                            isMulti
                            isClearable={false}
                            name="tags"
                            onChange={(newValue: any, actionMeta: any) => setSelectedTags(newValue.map((v: ListOptions) => ({ id: v.value, name: v.label })))}
                            options={tags.map((t) => ({ label: t.name, value: t.id }))}
                            placeholder={t('users.tags')}
                            styles={{
                                control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    width: "100%",
                                    borderRadius: "1.5rem",
                                    lineHeight: "1.5rem",
                                    padding: "0.3rem 1.25rem"
                                }),
                            }} />
                    </div>
                </div>
                { // if
                    (isAdmin || isSuperAdmin || isManager || isAccountHolder) &&
                    <div className={`${COMPONENT_CLASS}__fieldgroup`}>
                        <label htmlFor="symmioAccess">{t('users.enroll_in_wellness')}</label>
                        <SymmioLicenseCheckbox
                            organization={organization}
                            onClick={handleSymmioAccessCheckbox}
                            isChecked={isSymmioAccess}
                            numberOfAddedLicenses={newEmails.length}
                            numberOfRemovedLicenses={0}
                        />
                    </div>
                }
                { // if
                    newEmails != null && newEmails.length > 0 &&
                    <div className={`${COMPONENT_CLASS}__email-invites`}>
                        <Dialog.Title
                            as="h4">
                            {t('users.invitesToBeSent')}
                        </Dialog.Title>
                        <div>
                            {newEmails.map((email, i) => (<TagComponent
                                id={i.toString()}
                                onCloseClick={(index) => {
                                    const emails = [...newEmails];
                                    emails.splice(parseInt(index), 1);
                                    setNewEmails(emails);
                                    checkAccountHolderLimit(emails.length);
                                }}
                                key={`email-${i}`}
                            >{email}</TagComponent>))}
                        </div>
                    </div>
                }

            </Modal>
            <Modal
                isOpen={openDelete}
                isLoading={isSaving}
                onClose={setOpenDelete}
                title={t('buttons.btn_deleteUser')}
                defaultModalActions={true}
                submitButtonText={t('buttons.btn_deleteUserConfirm')}
                onSubmit={() => handleDeleteUserClick()}
                onCancel={() => setOpenDelete(false)}>
                <div>
                    <p>
                        {t('users.user_confirmDelete', { email: userToDelete?.email })}
                    </p>
                </div>
            </Modal>

        </div >
    );
}

export { Clients };